/**
 * Bridge between MuckaPanel (which owns the conversation) and the Mucka Bar
 * (which shows a docked transcript of it on mobile).
 *
 * Why a store and not props: `MuckaPanel` is mounted inside `AuthedShell`,
 * `MobileNav` is mounted in `app/layout.tsx` OUTSIDE it, so there is no shared
 * parent to thread state through. The codebase already solves this pair with a
 * window store plus an event (see `__muckaNavPerms` in `MobileNav`); this is
 * the same shape, typed, for the conversation.
 *
 * Why not lift the state instead: MuckaPanel owns SSE streaming, attachment
 * uploads, conversation persistence, action-card staging and approvals, all
 * interlocking. Publishing a read-only view of the messages gets the bar the
 * same transcript with none of that machinery moved, so a regression here
 * cannot break sending.
 *
 * Direction of travel:
 *   MuckaPanel --publish(messages, thinking)--> store --> bar renders them
 *   bar --requestSend(text)--> MuckaPanel sends it through its normal path
 *
 * The bar never mutates the conversation directly. It asks, MuckaPanel does.
 */

import type { ChoiceSet, DeleteAction } from '@/lib/engine/tools';
import type { VoiceJobListRow } from '@/lib/voice/transcript-storage';
import type { NavAction } from '@/lib/chat/nav-action';
import type { StagedAction } from '@/lib/engine/stage-action';
import type { StagedState } from '@/components/chat/BubbleBlock';
import type { UiBlock } from '@/lib/chat/ui-blocks';
import type { LineEditorState } from '@/components/chat/UiBlockView';

/** The subset of a chat message the bar draws. Not MuckaPanel's full
 *  `Message`, but no longer "text only" either: on a phone the bar is the chat,
 *  so anything a tool result tells the owner is on screen has to be here. */
export interface BarMessage {
  role: 'user' | 'mucka';
  text: string;
  time?: number;
  /**
   * Tap-to-answer chips (`ask_user`). An exception to the text-only rule above,
   * and a deliberate one: the bar is the DEFAULT mobile chat surface, so chips
   * that only render in the full panel are invisible to most of the people this
   * is for. They earn the exception by being nothing but labels — tapping one
   * sends that text through the ordinary path, so unlike an action card there
   * is no machinery to move and nothing to get wrong.
   */
  choices?: ChoiceSet;
  /** Which chip was tapped, once one has been. */
  choiceAnswer?: string;
  /**
   * "View Danny Cole" — the engine's go-and-look-at-this buttons. The same
   * exception as `choices`, on the same grounds and for the same reason it was
   * needed: the bar is the DEFAULT mobile chat surface, so a button that only
   * renders in the full panel is invisible to most of the people it is for.
   * They earn it by being a label and a destination — tapping one is a
   * `router.push`, so unlike an action card there is no machinery to move.
   *
   * Ask Mucka to find a client on a phone and the whole point of the answer is
   * the button underneath it; leaving this out made the reply a dead end.
   */
  navActions?: NavAction[];
  /** Rows for an on-screen job list, built server-side (job-list-rows.ts). */
  jobList?: VoiceJobListRow[];
  /**
   * 🚨 The approval card — send this invoice, record this payment.
   *
   * The third and most important exception to the text-only rule above, on the
   * same grounds as `choices` and `navActions` and for a worse reason: this is
   * the button Mucka's whole trust model rests on, and while it drew only in
   * the full panel it was invisible to everyone who talks to Mucka from the bar
   * mic — which on a phone is everyone. TKT-1325 is twenty minutes of an owner
   * being told to tap a card that was never on any screen he had open.
   *
   * It does not break "the bar never mutates the conversation": the card is
   * rendered here and the DECISION is dispatched back to MuckaPanel, which owns
   * the PATCH and the card's state, exactly as a tapped chip is.
   */
  stagedActions?: StagedAction[];
  /**
   * 🚨 The delete confirmation — "Delete it" / "Keep it".
   *
   * Missed by TKT-1325 for the same reason the approval card was missed before
   * it: it drew only in MuckaPanel. `propose_delete` on typed chat returns
   * "Delete confirmation shown to user… Waiting for their decision", which is
   * TRUE for the panel and false for the bar — and on a phone the bar is the
   * chat. One owner asked twice to remove a duplicate property, was told
   * both times "the delete button's up — tap it", saw nothing, and ended up in
   * a voice call raising TKT-1345 about it.
   *
   * Same rule as the approval card: drawn here, DECIDED in MuckaPanel, which
   * owns the DELETE request and `deleteStates`.
   */
  deleteAction?: DeleteAction;
  /**
   * Server-built components — a quote you can adjust, who owes you, the week
   * (`ui-blocks.ts`). Drawn here by the same renderer as the panel, and for the
   * same reason as everything above: on a phone the bar IS the chat. A step or
   * a Chase tap is dispatched back to MuckaPanel, which does the saving.
   */
  blocks?: UiBlock[];
  /** An attached photo, document or pin. Carried because these messages have
   *  NO text — without it the bar would render an empty bubble every time
   *  somebody sends a photo. */
  attachment?: {
    kind: 'image' | 'video' | 'document' | 'location';
    url?: string;
    filename?: string;
    uploading?: boolean;
    failed?: boolean;
  };
}

export interface BarChatSnapshot {
  messages: BarMessage[];
  /** True while Mucka is composing a reply — the bar shows a typing row. */
  thinking: boolean;
  /** What is being looked up, shown in the typing row instead of dots (progress-line.ts). */
  thinkingStatus?: string | null;
  /**
   * Each staged card's progress through its one decision, keyed by card id.
   *
   * Published rather than held per-surface because the same card can be on
   * screen in the bar and in the panel at once: approving it in one has to read
   * as approved in the other, or the owner is looking at a live Send button for
   * an email that has already gone.
   */
  stagedStates: Record<string, StagedState>;
  /**
   * Why a staged card's send was refused, keyed by card id, in the server's
   * own words. Present only for a card in the `error` state.
   *
   * 🚨 Without it the card said "That didn't go through. Nothing was sent."
   * and nothing else, while the route had answered with the reason and the
   * one thing that fixes it (TKT-1481: an address not saved on the client).
   * Optional so a snapshot built without it still type-checks; a missing
   * entry falls back to the stock line.
   */
  stagedErrors?: Record<string, string>;
  /**
   * Each delete confirmation's state, keyed by MESSAGE INDEX (that is how
   * MuckaPanel keys it — a deleteAction has no id of its own). Safe to share
   * because the bar renders the very array MuckaPanel publishes, so an index
   * means the same message on both surfaces.
   */
  deleteStates: Record<number, DeleteState>;
  /**
   * Each quote line editor's save state, keyed by quote id. Published for the
   * same reason as `stagedStates`: the editor can be on screen in the bar and
   * the panel at once, and both must say "Saving…" or show the same error.
   * Optional so a snapshot built without it still type-checks.
   */
  editorStates?: Record<string, LineEditorState>;
  /** Whether this person may change a quote. Without it the editor is read-only. */
  canEditQuotes?: boolean;
}

export type DeleteState = 'idle' | 'deleting' | 'deleted' | 'dismissed';

const EMPTY: BarChatSnapshot = { messages: [], thinking: false, stagedStates: {}, deleteStates: {} };

let snapshot: BarChatSnapshot = EMPTY;
const listeners = new Set<() => void>();

/** Ask the bar to send `text` through MuckaPanel's normal send path. */
export const BAR_SEND_EVENT = 'mucka-bar-send';
/** Ask MuckaPanel to start the conversation over. */
export const BAR_RESET_EVENT = 'mucka-bar-reset';
/** Ask MuckaPanel to answer an `ask_user` question with the tapped chip. */
export const BAR_CHOICE_EVENT = 'mucka-bar-choice';
/** Ask MuckaPanel to run one of its four attach actions. The pickers, the
 *  upload pipeline and the hidden file inputs all live there; the inputs sit
 *  outside its <aside>, so they work whether the panel is open or not. */
export const BAR_ATTACH_EVENT = 'mucka-bar-attach';
export type BarAttachKind = 'photos' | 'camera' | 'document' | 'location';
/** Ask VoiceProvider to start a session WITHOUT opening the full-screen panel. */
export const BAR_VOICE_START_EVENT = 'mucka-bar-voice-start';
/**
 * Tell the bar a search field elsewhere in the tree has taken or lost focus,
 * so it can stand its tabs down the way it does for its own composer.
 *
 * Dispatch `{ open: true }` on focus and `{ open: false }` on blur. It must be
 * FOCUS that drives this, never keyboard detection: the Android WebView cannot
 * detect the keyboard reliably (see the `typing` note in MobileNav), and there
 * the bar rides the keyboard on its own because `dvh` already reports the
 * shrunk viewport — so a bar left standing covers the results.
 */
export const BAR_SEARCH_EVENT = 'mucka-bar-search-focus';
/**
 * Ask AuthedShell to open the full-screen panel (the "open full chat" route).
 *
 * ⚠️ AuthedShell listens for this and NOTHING DISPATCHES IT. That used to be a
 * hole — a reply carrying anything the bar could not draw had no route out of
 * the text-only transcript — and a control was briefly added for it. It is gone
 * again, on purpose: a button whose only job is "see this somewhere else" is a
 * button admitting the surface you are on is the wrong one, so the answer is to
 * draw the thing here. ⚠️ "The bar draws everything" was written beside this
 * and was false — the delete confirmation was missing until TKT-1345, and the
 * fields still missing are listed, with reasons, in
 * `the-delete-card-reaches-the-bar.test.tsx`, which fails on any new one. The listener stays because the panel is still reachable and something
 * may yet want to open it; if you wire a sender, wire it to a real need.
 */
export const BAR_EXPAND_EVENT = 'mucka-bar-expand';
/**
 * Open the bar's composer — the docked transcript comes with it, because
 * `showTranscript` requires the composer to be out.
 *
 * 🚨 Needed by anything that starts a call from OUTSIDE the bar. The mic lives
 * inside the composer, so a bar-started call always has it open; a call started
 * anywhere else lands on `barMinimised` — an orange banner and no transcript,
 * with Mucka talking to somebody who can see nothing of what is being said.
 * "Set up with Mucka" is exactly that case, and it is an owner's FIRST
 * conversation.
 */
export const BAR_OPEN_EVENT = 'mucka-bar-open';

/**
 * Open the composer, optionally with words already typed in it — the new-look
 * sheet's "Tell the team" starts the message and leaves the rest to them.
 * Never sends: what goes to Mucka is still what they choose to send.
 */
export function requestBarOpen(draft?: string) {
  window.dispatchEvent(new CustomEvent(BAR_OPEN_EVENT, { detail: draft ? { draft } : undefined }));
}

export function publishChat(next: BarChatSnapshot) {
  // Identity check first: MuckaPanel re-renders on every keystroke in its own
  // composer, and an unchanged array would still notify every subscriber and
  // re-render the bar's transcript on each one.
  if (
    snapshot.thinking === next.thinking &&
    snapshot.thinkingStatus === next.thinkingStatus &&
    snapshot.stagedStates === next.stagedStates &&
    snapshot.stagedErrors === next.stagedErrors &&
    snapshot.deleteStates === next.deleteStates &&
    snapshot.editorStates === next.editorStates &&
    snapshot.canEditQuotes === next.canEditQuotes &&
    snapshot.messages.length === next.messages.length &&
    snapshot.messages.every((m, i) => m === next.messages[i])
  ) {
    return;
  }
  snapshot = next;
  listeners.forEach((l) => l());
}

export function subscribeChat(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function getChatSnapshot(): BarChatSnapshot {
  return snapshot;
}

/** Server snapshot for useSyncExternalStore — always empty, the conversation
 *  lives in sessionStorage and only exists after hydration. Must be a stable
 *  reference or React loops on it. */
export function getServerChatSnapshot(): BarChatSnapshot {
  return EMPTY;
}

/** Reset on sign-out / workspace switch so a conversation can't leak across. */
export function clearChat() {
  publishChat(EMPTY);
}

export function requestBarSend(text: string) {
  window.dispatchEvent(new CustomEvent(BAR_SEND_EVENT, { detail: { text } }));
}

/**
 * A chip tapped in the bar. Deliberately NOT `requestBarSend(label)`: the panel
 * owns the transcript, so it has to mark that message answered as well as send
 * the text. Routing it as a plain send would fire the message and leave the
 * chips live underneath it, tappable a second time.
 */
export function requestBarChoicePick(messageIndex: number, label: string) {
  window.dispatchEvent(
    new CustomEvent(BAR_CHOICE_EVENT, { detail: { messageIndex, label } }),
  );
}

/** Ask MuckaPanel to decide a staged approval card tapped in the bar. */
export const BAR_STAGED_EVENT = 'mucka-bar-staged';
export type BarStagedDecision = 'approved' | 'dismissed';

/**
 * An approval tapped in the bar. Deliberately NOT a fetch from here: MuckaPanel
 * owns the PATCH, the card's state and the `router.refresh()` that follows a
 * send, and a second caller doing its own request would leave the panel's copy
 * of the same card sitting on a live Send button.
 */
export function requestBarStagedDecision(id: string, decision: BarStagedDecision) {
  window.dispatchEvent(new CustomEvent(BAR_STAGED_EVENT, { detail: { id, decision } }));
}

/** Ask MuckaPanel to save a step tapped on a quote line editor in the bar. */
export const BAR_LINE_STEP_EVENT = 'mucka-bar-line-step';

/**
 * A + or − tapped on the bar's line editor. Not a fetch from here: MuckaPanel
 * owns the save queue, the block's new totals and the state both copies of the
 * editor read from, exactly as it owns a staged decision.
 */
export function requestBarLineStep(quoteId: string, lineId: string, quantity: number) {
  window.dispatchEvent(new CustomEvent(BAR_LINE_STEP_EVENT, { detail: { quoteId, lineId, quantity } }));
}

/** Ask MuckaPanel to act on a delete confirmation tapped in the bar. */
export const BAR_DELETE_EVENT = 'mucka-bar-delete';
export type BarDeleteDecision = 'delete' | 'keep';

/**
 * "Delete it" or "Keep it" tapped in the bar. Deliberately NOT a fetch from
 * here, for the reason `requestBarStagedDecision` gives: MuckaPanel owns the
 * DELETE, the refusal message when the server says no, and the state both
 * copies of the strip read from.
 */
export function requestBarDelete(messageIndex: number, decision: BarDeleteDecision) {
  window.dispatchEvent(new CustomEvent(BAR_DELETE_EVENT, { detail: { messageIndex, decision } }));
}
